// src/context/userContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { useLocation } from 'react-router-dom';

const UserContext = createContext();
export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [balance, setBalance] = useState(0);
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTwo, setLoadingTwo] = useState(true);
  const [refBonus, setRefBonus] = useState(0);
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [premium, setPremium] = useState(false);
  const [error, setError] = useState(null);
  const [openInfoThree, setOpenInfoThree] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [telegramData, setTelegramData] = useState(null);
  const location = useLocation();

  // Получаем Telegram пользователя
  const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  
  // Сохраняем данные из Telegram в состоянии
  useEffect(() => {
    if (telegramUser) {
      setTelegramData({
        id: telegramUser.id,
        username: telegramUser.username,
        firstName: telegramUser.first_name,
        lastName: telegramUser.last_name,
        isPremium: telegramUser.is_premium
      });
    }
  }, [telegramUser]);

  const fetchData = async (userId) => {
    if (!userId) {
      console.log('fetchData called with no userId');
      return;
    }

    try {
      console.log('Fetching user data for:', userId);
      const userRef = doc(db, 'telegramUsers', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log('User data found:', data);
        
        // Устанавливаем данные из Firestore
        setBalance(data.balance || 0);
        setRefBonus(data.refBonus || 0);
        setPremium(data.isPremium || false);
        setReferrals(data.referrals || []);
        
        // Если в Firestore есть username и fullName - используем их
        // Иначе используем данные из Telegram
        if (data.username || telegramUser?.username) {
          setUsername(data.username || telegramUser?.username || '');
        }
        
        if (data.fullName || telegramUser?.first_name || telegramUser?.last_name) {
          const fullNameFromTelegram = `${telegramUser?.first_name || ''} ${telegramUser?.last_name || ''}`.trim();
          setFullName(data.fullName || fullNameFromTelegram || '');
        }
        
        setId(userId);
      } else {
        console.log('User document does not exist');
        // Если документа нет, устанавливаем данные из Telegram
        if (telegramUser) {
          setUsername(telegramUser.username || '');
          const fullNameFromTelegram = `${telegramUser.first_name || ''} ${telegramUser.last_name || ''}`.trim();
          setFullName(fullNameFromTelegram);
          setPremium(telegramUser.is_premium || false);
        }
        setId(userId);
      }
    } catch (err) {
      console.error('Ошибка при получении данных:', err);
      setError('Не удалось загрузить данные пользователя.');
    }

    setLoading(false);
  };

  const sendUserData = async () => {
    console.log('sendUserData called, telegramUser:', telegramUser);
    
    if (!telegramUser) {
      console.log('No telegramUser available');
      setLoading(false);
      setInitialized(true);
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    let referrerId = queryParams.get("ref")?.replace(/\D/g, "");

    const { id: userId, username: tgUsername, first_name, last_name, is_premium } = telegramUser;
    console.log('Telegram user ID:', userId);

    if (!userId) {
      console.log('No userId from Telegram');
      setLoading(false);
      setInitialized(true);
      return;
    }

    // Сразу устанавливаем данные из Telegram в состояние
    const finalUsername = tgUsername || `${first_name}_${userId}`;
    const fullNamed = `${first_name} ${last_name || ''}`.trim();
    
    // Устанавливаем в состояние ДО сохранения в Firestore
    setUsername(finalUsername);
    setFullName(fullNamed);
    setPremium(is_premium || false);
    setId(userId.toString());

    const userRef = doc(db, 'telegramUsers', userId.toString());
    
    try {
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        console.log('Creating new user document');
        const userData = {
          userId: userId.toString(),
          username: finalUsername,
          firstName: first_name,
          lastName: last_name || '',
          fullName: fullNamed,
          balance: 0,
          isPremium: is_premium || false,
          lastActive: new Date(),
          refereeId: referrerId || null,
          referrals: [],
          favorites: [],
          createdAt: new Date()
        };

        await setDoc(userRef, userData);
        
        if (referrerId) {
          try {
            const referrerRef = doc(db, 'telegramUsers', referrerId);
            const referrerDoc = await getDoc(referrerRef);
            if (referrerDoc.exists()) {
              await updateDoc(referrerRef, {
                referrals: arrayUnion({
                  userId: userId.toString(),
                  username: finalUsername,
                  balance: 0,
                  level: { id: 1, name: 'Bronze', imgUrl: '/bronze.webp' },
                }),
              });
              console.log('Referrer updated');
            }
          } catch (referrerErr) {
            console.error('Error updating referrer:', referrerErr);
          }
        }
      } else {
        console.log('User document already exists');
        // Обновляем lastActive и обновляем данные из Telegram если они изменились
        const updateData = {
          lastActive: new Date()
        };
        
        // Проверяем, изменились ли данные пользователя в Telegram
        const existingData = userDoc.data();
        if (existingData.username !== finalUsername) {
          updateData.username = finalUsername;
        }
        if (existingData.fullName !== fullNamed) {
          updateData.fullName = fullNamed;
        }
        if (existingData.isPremium !== is_premium) {
          updateData.isPremium = is_premium || false;
        }
        
        await updateDoc(userRef, updateData);
      }

      // Загружаем полные данные пользователя из Firestore
      await fetchData(userId.toString());
      
    } catch (err) {
      console.error('Ошибка при создании/обновлении пользователя:', err);
      setError('Ошибка при сохранении данных пользователя.');
    } finally {
      setInitialized(true);
    }
  };

  // Функция для принудительного обновления данных пользователя из Telegram
  const refreshUserData = () => {
    if (telegramUser) {
      const { username: tgUsername, first_name, last_name, is_premium } = telegramUser;
      const finalUsername = tgUsername || `${first_name}_${telegramUser.id}`;
      const fullNamed = `${first_name} ${last_name || ''}`.trim();
      
      setUsername(finalUsername);
      setFullName(fullNamed);
      setPremium(is_premium || false);
      
      // Также обновляем в Firestore
      if (id) {
        updateUserDataInFirestore(id, {
          username: finalUsername,
          fullName: fullNamed,
          isPremium: is_premium || false,
          lastActive: new Date()
        });
      }
    }
  };

  // Вспомогательная функция для обновления данных в Firestore
  const updateUserDataInFirestore = async (userId, data) => {
    try {
      const userRef = doc(db, 'telegramUsers', userId);
      await updateDoc(userRef, data);
      console.log('User data updated in Firestore');
    } catch (err) {
      console.error('Error updating user data in Firestore:', err);
    }
  };

  useEffect(() => {
    console.log('UserProvider mounted');
    sendUserData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (id) {
      console.log('User ID changed:', id);
      fetchData(id);
    }
  }, [id]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.log('Loading timeout reached');
        setLoading(false);
      }
    }, 3000);
    
    return () => clearTimeout(timeout);
  }, [loading]);

  return (
    <UserContext.Provider value={{
      balance, setBalance,
      id,
      loading,
      loadingTwo, setLoadingTwo,
      refBonus, setRefBonus,
      telegramUser,
      fullName, setFullName,
      username, setUsername,
      premium, setPremium,
      error, setError,
      location,
      openInfoThree, setOpenInfoThree,
      initialized,
      referrals, setReferrals,
      refreshUserData, // Экспортируем функцию обновления
      telegramData // Экспортируем данные Telegram
    }}>
      {children}
    </UserContext.Provider>
  );
};